import React from "react";
import Icon from "~/components/shared/Icon";
import { createPortal } from "react-dom";

interface Props {
  children: React.ReactNode;
  closeable: boolean;
  isOpen?: boolean;
  width?: number | string;
  dialogStyle?: React.CSSProperties;
  onRequestClose?: () => void;
  onHide?: () => void;
  onClose?: () => void;
}

interface State {
  rendered: boolean;
  visible: boolean;
  firstElement: HTMLElement | null;
  lastElement: HTMLElement | null;
}

// eslint-disable-next-line react/require-optimization
class Dialog extends React.Component<Props, State> {
  static defaultProps = {
    closeable: true,
    isOpen: false,
    width: 500,
    dialogStyle: {},
  };

  dialogRef: React.RefObject<HTMLDivElement> = React.createRef();

  constructor(initialProps: Props) {
    super(initialProps);

    let rendered = false;
    let visible = false;

    if (initialProps.isOpen) {
      rendered = visible = true;
    }
    this.state = {
      rendered,
      visible,
      firstElement: null,
      lastElement: null,
    };
  }

  componentDidMount() {
    document.documentElement.addEventListener("keydown", this.handleDocumentKeyDown, false);
  }

  componentWillUnmount() {
    document.documentElement.removeEventListener("keydown", this.handleDocumentKeyDown);
    document.body.classList.remove("overflow-hidden");
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    // Opening the dialog
    if (!this.props.isOpen && nextProps.isOpen) {
      this.setState({ rendered: true }, () => {
        this.setState({ visible: true });
        this.updateFocus();
      });
    }

    // Closing the dialog
    if (this.props.isOpen && !nextProps.isOpen) {
      this.setState({ visible: false }, () => {
        // Give the animation some time to finish, then remove the dialog from
        // the DOM
        setTimeout(() => {
          this.setState({ rendered: false, firstElement: null, lastElement: null }, () => {
            this.props.onHide && this.props.onHide();
          });
          this.props.onClose && this.props.onClose();
        }, 150);
      });
    }
  }

  componentDidUpdate() {
    // This locks the body's scrolling whenever the dialog is _rendered_
    const action = this.state.rendered ? "add" : "remove";
    document.body.classList[action]("overflow-hidden");
  }

  /* this.props.children often contains a field with an autofocus: true attribute,
  but this is overwritten by the existing focus on the button used to open the dialog.
  This function is necessary to ensure the focus shifts to the first interactive element
  inside the dialog.*/
  updateFocus = () => {
    // Find the ref to the currently open dialog
    const dialogComponent = this.dialogRef.current;

    // Find the first clickable element inside the dialog.
    // This may be a text field or the submit button
    if (dialogComponent) {
      const elements = dialogComponent.querySelectorAll(
        'a[href], select, textarea, input:not([type="hidden"]), button',
      );

      if (elements.length > 0) {
        this.setState({
          firstElement: elements[0] as HTMLElement,
          lastElement: elements[elements.length - 1] as HTMLElement,
        });

        (elements[0] as HTMLElement).focus();
      }
    }
  };

  maybeClose = (event: Event) => {
    event.preventDefault();
    if (typeof this.props.onRequestClose === "function") {
      this.props.onRequestClose();
    }
  };

  handleCloseClick = (event: React.MouseEvent) => {
    this.maybeClose(event.nativeEvent);
  };

  handleDocumentKeyDown = (event: KeyboardEvent) => {
    // Disallow closing via escape if dialog is not closeable
    if (!this.props.closeable) {
      return;
    }

    // Close the dialog on hitting the escape key
    if (this.state.visible && event.key === "Escape") {
      this.maybeClose(event);
    }

    // traps focus within the dialog component
    if (this.state.visible && event.key === "Tab") {
      const { firstElement, lastElement } = this.state;

      // If the first element is focused and the user tries to
      // tab backwards, focus the last element in the dialog
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
        // otherwise when the user is on the last element, move the focus to the first
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    }
  };

  renderBackdrop() {
    return (
      <div
        className={`not-inverted dark:bg-charcoal-900/90 absolute inset-0 bg-white/90 ${
          this.state.visible
            ? "animate-in fade-in-0 duration-150"
            : "animate-out fade-out-0 duration-150"
        }`}
      />
    );
  }

  renderCloseButton() {
    if (!this.props.closeable) {
      return;
    }

    return (
      <button
        className="shadow-subtle not-inverted no-title-tooltip dark:bg-charcoal-900 focus:outline-hidden absolute -right-4 -top-4 flex h-8 w-8 cursor-pointer items-center rounded-full border border-white bg-white p-0 hover:text-lime-500 focus:text-lime-500 dark:border-white/10 dark:text-white"
        onClick={this.handleCloseClick}
        aria-label="Close dialog"
        title="Close dialog"
      >
        <Icon className="mx-auto" icon="close" title="Close" />
      </button>
    );
  }

  renderDialog() {
    const dialogStyle = {
      ...this.props.dialogStyle,
      width: this.props.width,
    };

    return (
      <div
        className={`after:flex-0 relative flex h-full w-screen max-w-full flex-col overflow-auto scroll-smooth p-6 after:block after:h-[40px] after:content-[''] ${
          this.state.visible
            ? "animate-in fade-in-0 slide-in-from-bottom-[15px] duration-300"
            : "animate-out fade-out-0 duration-150"
        }`}
        role="dialog"
        ref={this.dialogRef}
      >
        <div
          className="shadow-subtle relative m-auto max-w-full rounded-lg bg-white [&_label]:break-words"
          style={dialogStyle}
        >
          {this.props.children}
          {this.renderCloseButton()}
        </div>
      </div>
    );
  }

  render() {
    if (!this.state.rendered) {
      return null;
    }

    return createPortal(
      <div className="fixed inset-0 z-[1000]">
        {this.renderBackdrop()}
        {this.renderDialog()}
      </div>,
      document.body,
    );
  }
}

export default Dialog;
